import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import currencyFormat from '../../utils/currency';
import { localize } from '../../i18n';

const RoomService = function RoomService(props) {
  const {
    body,
    configs,
    salesTaxEnabled,
  } = props;

  const orderRestaurant = _.find(body, { key: 'restaurantId' });
  const orderMenu = _.find(body, { key: 'menus' });

  const payments = {
    card: props.t('Credit card'),
    cash: props.t('Cash'),
    room: props.t('Room charge'),
  };
  const paymentObj = _.find(body, { key: 'paymentMethod' });
  const payment = paymentObj ? payments[paymentObj.value] : null;

  if (configs[0]) {
    const affiliateMenuConfig = configs[0].affiliate_menu.menu_config;
    const menuConfigs = affiliateMenuConfig.filter(configItem => configItem.restaurant.restaurant_id === orderRestaurant.value);
    const restaurantConfig = menuConfigs[0] ? menuConfigs[0].restaurant : [];
    const menusConfig = menuConfigs[0] ? menuConfigs[0].menus : [];
    const restaurant = _.find(body, { key: 'restaurantName' });
    const fees = [{
      name: 'affiliate_charge',
      type: 'fee',
      label: props.t('Hotel Service Fee'),
      amount: 0,
      isPercent: false,
      isVendorFee: false,
      isGroupBoundary: true,
      display: false,
      weight: 4,
    }, {
      name: 'delivery_charge',
      type: 'fee',
      label: props.t('Vendor Delivery Fee'),
      amount: 0,
      isPercent: false,
      isVendorFee: true,
      isGroupBoundary: true,
      display: false,
      weight: 2,
    }, {
      name: 'service_charge',
      type: 'fee',
      label: props.t('Vendor Service Fee'),
      amount: 0,
      isPercent: false,
      isVendorFee: true,
      isGroupBoundary: false,
      display: false,
      weight: 1,
    }];

    const chargesMapper = (charge) => {
      if (restaurantConfig[charge]) {
        _.each(fees, (item, idx) => {
          const itemEdited = item;
          if (item.name === charge) {
            itemEdited.amount = restaurantConfig[charge].amount;
            itemEdited.isPercent = restaurantConfig[charge].is_percent;
            if (restaurantConfig[charge].amount > 0) {
              itemEdited.display = true;
            }
          }
          fees[idx] = itemEdited;
        });
      }
    };

    _.each(fees, item => chargesMapper(item.name));

    const calculateItemTotal = (menus, restaurantConfiguration) => {
      const restaurantSalesTax = restaurantConfiguration.sales_tax;
      const editedMenus = menus;
      let total = 0;
      let totalTax = 0;
      let totalWithTax = 0;
      let taxIncluded = false;
      let salesTax;

      _.each(editedMenus, (menu, idx) => {
        let menuTotal = 0;

        _.each(menu.items, (item) => {
          let itemTotal = item.itemPrice;
          if (item.customizations && item.customizations.length) {
            _.each(item.customizations, (customization) => {
              if (customization.selectedOptions && customization.selectedOptions.length) {
                _.each(customization.selectedOptions, (selectedOption) => {
                  itemTotal += selectedOption.optionPrice;
                });
              }
            });
          }
          menuTotal += (itemTotal * item.orderedCount);
        });

        const currentMenuConfig = _.find(menusConfig, { menu_id: menu.menuId });

        salesTax = currentMenuConfig && _.isNumber(currentMenuConfig.sales_tax) ? currentMenuConfig.sales_tax : restaurantSalesTax;

        // Considering sales tax "zero" as appropriate for output Total tax label.
        if (salesTaxEnabled && _.isNumber(salesTax)) {
          taxIncluded = true;
        } else {
          salesTax = 0;
        }

        const menuTax = (menuTotal * salesTax) / 100;
        const menuTotalWithTax = menuTotal + menuTax;

        total += menuTotal;
        totalTax += menuTax;
        totalWithTax += menuTotalWithTax;

        editedMenus[idx].total = {
          menuTotal,
          tax: menuTax,
          menuTotalWithTaxes: menuTotalWithTax,
        };
        editedMenus[idx].config = currentMenuConfig;
      });

      return {
        total,
        totalTax,
        totalWithTax,
        taxIncluded,
      };
    };

    const total = calculateItemTotal(orderMenu.value, restaurantConfig);
    const itemTotal = total.total;

    const tax = {
      value: total.totalTax,
      isPercent: false,
      display: total.taxIncluded,
    };

    const calculateTotal = (itemTotalCalculate, feesCalculate, feeType) => {
      let totalCalculate = total.totalWithTax;

      _.each(_.filter(feesCalculate, feeType), (fee) => {
        let feeAmount;

        if (fee.isPercent) {
          feeAmount = itemTotal * (fee.amount / 100);
        } else {
          feeAmount = fee.amount;
        }

        totalCalculate += feeAmount;
      });

      return totalCalculate;
    };

    const totals = [{
      name: 'itemTotal',
      type: 'total',
      label: props.t('Item Total'),
      amount: itemTotal,
      isPercent: false,
      isGroupBoundary: true,
      display: true,
      weight: 0,
    }, {
      name: 'order_total',
      type: 'total',
      label: props.t('Order Total'),
      amount: calculateTotal(itemTotal, fees, 'isVendorFee'),
      isPercent: false,
      isGroupBoundary: true,
      display: true,
      weight: 3,
    }, {
      name: 'sales_tax',
      type: 'fee',
      label: props.t('Estimated Total Tax'),
      amount: tax.value,
      isPercent: tax.isPercent,
      isVendorFee: false,
      isGroupBoundary: true,
      display: tax.display,
      weight: 2,
    }, {
      name: 'grand_total',
      type: 'total',
      label: props.t('Total Billed'),
      amount: calculateTotal(itemTotal, fees, 'display'),
      isPercent: false,
      isGroupBoundary: false,
      display: true,
      weight: 5,
    }];

    let lineItems = totals.concat(fees);

    const formatOrderCharge = (amount, isPercent, itemTotalFormat) => {
      const price = isPercent ? itemTotalFormat * (amount / 100) : amount;

      return price;
    };

    const hasCustomizations = (menuItem) => {
      let showExtras = false;
      if (!_.isEmpty(menuItem.customizations)) {
        _.each(menuItem.customizations, (type) => {
          if (!_.isEmpty(type.selectedOptions)) {
            showExtras = true;
          }
        });
      }
      return showExtras;
    };

    const currency = restaurantConfig.currency;

    const mapCustomItems = items => items.map(custom => (
      <table key={custom.customizationName} className="table customization-group">
        <tbody>
          <tr className="order-customizations">
            <td className="customization-name">{custom.customizationName}</td>
          </tr>
          <tr>
            <td className="customization-details">
              <table className="table">
                <tbody>
                  {custom.selectedOptions.map(option => (
                    <tr key={option.optionName}>
                      <td className="customization-choice"><i className="material-icons">&#xE315;</i>{option.optionName}</td>
                      <td className="value-type-price item-price">
                        {currencyFormat(option.optionPrice, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    ));

    const chargeClass = (item) => {
      let name = 'charge';
      if (item.isGroupBoundary) {
        name = `${name} last-in-group`;
      }
      if (item.name === 'order_total') {
        name = `${name} order-total`;
      }
      if (item.type === 'total') {
        name = `${name}  type-total`;
      }
      if (item.name === 'grand_total') {
        name = `${name} grand-total`;
      }
      return name;
    };

    const mapLineItems = (items) => {
      const itemsMapped = items.map((item) => {
        if ((item.amount === 0 && item.display) || item.amount !== 0) {
          return (
            <tr key={item.label} className={chargeClass(item)}>
              <td className="charge-label">{item.label}:</td>
              <td className="charge-amount">
                {currencyFormat(formatOrderCharge(item.amount, item.isPercent, itemTotal), currency)}
              </td>
            </tr>
          );
        }
        return null;
      });
      return (
        <table className="table">
          <tbody>
            {itemsMapped}
          </tbody>
        </table>
      );
    };

    const mapMenuItems = items => items.map((item, index) => (
      <div key={index}>
        <table className="table">
          <tbody>
            <tr className="item-ordered">
              <td className="value-type-price item-count">{item.orderedCount}</td>
              <td className="item-name">
                <div>{item.itemName}</div>
                {(item.instructions) ? (
                  <div className="order-instructions">
                    <strong>{props.t('Special Instructions')}: </strong>
                    <span className="text">{item.instructions}</span>
                  </div>
                ) : null }
              </td>
              <td className="value-type-price item-price">
                {currencyFormat((item.itemPrice * item.orderedCount), currency)}
              </td>
            </tr>
            {(hasCustomizations(item)) ? (
              <tr className="item-extras">
                <td />
                <td colSpan="2">
                  {mapCustomItems(item.customizations)}
                </td>
              </tr>
            ) : null }
          </tbody>
        </table>
      </div>
    ));


    const mappedMenus = orderMenu.value.map(item => (
      <div key={item.menuId}>
        <h4 className="menu-name">{props.t('Menu: {itemMenuName}', { itemMenuName: item.menuName })}</h4>
        {mapMenuItems(item.items)}
      </div>
    ));

    lineItems = _.sortBy(lineItems, ['weight']);

    return (
      <div className="uk-grid">
        <div className="uk-width-medium-2-5 uk-width-small-1-3 uk-width-mobile-1-1 form-heading">
          <h4 className="uk-margin-small-bottom">{props.t('Details')}</h4>
        </div>
        <div className="room-service-charges uk-width-medium-3-5 uk-width-small-2-3 uk-width-mobile-1-1 form-value dont-break-out">
          <h3 className="restaurant-name">
            <i className="material-icons">&#xE56C;</i>
            <span>{restaurant.value}</span>
          </h3>
          {mappedMenus}
          {mapLineItems(lineItems)}
          {(!tax.display) ? <div className="tax-label">{props.t('Tax is not included')}</div> : null }
        </div>
        {(payment) ? [
          <div key="payment-label" className="uk-width-medium-2-5 uk-width-small-1-3 uk-width-mobile-1-3 form-heading">
            <h4 className="uk-margin-small-bottom">{props.t('Payment Method')}</h4>
          </div>,
          <div key="payment-value" className="uk-width-medium-3-5 uk-width-small-2-3 uk-width-mobile-2-3 form-value dont-break-out">
            {payment}
          </div>,
        ] : null }
      </div>
    );
  }
  return null;
};

RoomService.propTypes = {
  // val: React.PropTypes.arrayOf(React.PropTypes.any),
  // label: React.PropTypes.string,
  body: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  configs: PropTypes.arrayOf(PropTypes.any),
  salesTaxEnabled: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default localize(RoomService);



// WEBPACK FOOTER //
// ./src/components/FormConfigs/RoomService.js